import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';

interface SavedReport {
  id: number;
  name: string;
  tags: string;
}

export const useReports = () => {
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSavedReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<SavedReport[]>('/api/saved-reports');
      setSavedReports(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch saved reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const saveReport = useCallback(async (name: string, tags: string[]) => {
    const result = await apiClient.post('/api/saved-reports', { name, tags });
    await fetchSavedReports();
    return result;
  }, [fetchSavedReports]);

  const deleteReport = useCallback(async (id: number) => {
    await apiClient.delete(`/api/saved-reports/${id}`);
    await fetchSavedReports();
  }, [fetchSavedReports]);

  const searchEmailTracking = useCallback(async (email: string) => {
    return apiClient.get(`/api/email-tracking/search?email=${encodeURIComponent(email)}`);
  }, []);

  const getSubscriberCount = useCallback(async (tags: string[], domain?: string) => {
    const tagQuery = tags.length > 0 ? `&tags=${tags.join(',')}` : '';
    const domainQuery = domain ? `&domain=${domain}` : '';
    const data = await apiClient.get(`/api/subscribers?getAllIds=true${tagQuery}${domainQuery}`) as any;
    return data.subscriberIds.length;
  }, []);

  useEffect(() => {
    fetchSavedReports();
  }, [fetchSavedReports]);

  return {
    savedReports,
    loading,
    error,
    saveReport,
    deleteReport,
    searchEmailTracking,
    getSubscriberCount,
    refetch: fetchSavedReports,
  };
};